// Dependencies
import React from "react"
import { graphql, useStaticQuery } from "gatsby"
// import { BLOCKS, MARKS } from "@contentful/rich-text-types";
// import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, MARKS, INLINES } from "@contentful/rich-text-types"
import { renderRichText } from "gatsby-source-contentful/rich-text"
import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer"

// Hooks
import useRandomItem from "../hooks/useRandomItem"
// SVGs
// import Arrows from "../annotations/Arrows"
// import Rings from "../annotations/Rings"
// import Underlines from "../annotations/Underlines"
// import Squiggles from "../annotations/Squiggles"
import { Underline, Ring, Arrow, Squiggle } from "./AnnotatedText"
import Accordian from "../components/Accordian"
import Markdown from "../components/Markdown"
import { Image } from "@unpic/react"
import ImageGallery from "../components/ImageGallery"

const CONTENT_TYPES = {
  FAQ: "ContentfulPageSectionFaq",
  EMBED: "ContentfulEmbedIframe",
  GALLERY: "ContentfulImageGallery",
  ASSET: "ContentfulAsset",
}

const EmbeddedEntry = ({ data }) => {
  const type = data?.target?.__typename

  if (type === CONTENT_TYPES.FAQ) {
    const entry = data.target
    return (
      <Accordian label={() => <h1>{entry.question}</h1>}>
        <div className="grid innerx2" style={{ paddingBottom: "4rem" }}>
          <Markdown field={entry.answer} className="has-dark-links p2 col-4" />
          <div className="col-2"></div>
        </div>
      </Accordian>
    )
  } else if (type === CONTENT_TYPES.EMBED) {
    const entry = data.target
    // console.log(entry.code.raw)
    // Extract width and height from the code, it could look like width='100' or width="100"
    let [, width] = entry?.code?.raw.match(/width=['"]?(\d+)/) ?? []
    let [, height] = entry?.code?.raw.match(/height=['"]?(\d+)/) ?? []
    width = width.endsWith("%") ? undefined : parseInt(width)
    height = height.endsWith("%") ? undefined : parseInt(height)
    const aspectRatio = width && height ? width / height : undefined

    return (
      <div
        className="embed-iframe outerx2"
        dangerouslySetInnerHTML={{ __html: entry.code.raw }}
        style={{ aspectRatio, width: "100%", height: "auto" }}
      />
    )
  } else if (type === CONTENT_TYPES.GALLERY) {
    const entry = data.target
    return (
      <ImageGallery
        className=""
        images={entry.images}
        caption={entry.caption}
      />
    )
  } else {
    return null
  }
}

const Asset = ({ data }) => {
  const entry = data?.target
  if (!entry) return null
  const file = entry.file
  const isVideo = file.contentType === "video/mp4"
  return file && isVideo ? (
    <div className="article-asset">
      <video src={file.url} />
    </div>
  ) : file ? (
    <div className="article-asset">
      <Image
        src={file.url ? `${file.url}?w=1440&fm=webp` : ""}
        alt={entry.description || ""}
      />
      {entry.description && <figcaption>{entry.description}</figcaption>}
    </div>
  ) : (
    <img className="debug" alt="Invalid image" />
  )
}

const RichTextContent = ({ content, color, palettes }) => {
  // console.log(content)
  return (
    content &&
    renderRichText(content, {
      renderMark: {
        [MARKS.UNDERLINE]: text => (
          <Underline color={color} palettes={palettes}>
            {text}
          </Underline>
        ),
        [MARKS.BOLD]: text => (
          <Ring color={color} palettes={palettes}>
            {text}
          </Ring>
        ),
        [MARKS.CODE]: text => (
          <Arrow color={color} palettes={palettes}>
            {text}
          </Arrow>
        ),
        [MARKS.ITALIC]: text => (
          <Squiggle color={color} palettes={palettes}>
            {text}
          </Squiggle>
        ),
      },
      renderNode: {
        [BLOCKS.PARAGRAPH]: (node, children) => <p>{children}</p>,
        [BLOCKS.EMBEDDED_ENTRY]: (node, children) => (
          <EmbeddedEntry {...node}>{children}</EmbeddedEntry>
        ),
        [BLOCKS.EMBEDDED_ASSET]: (node, children) => (
          <Asset {...node}>{children}</Asset>
        ),
      },
      // renderText: text =>
      //   text
      //     .split("\n")
      //     .reduce(
      //       (children, textSegment, index) => [
      //         ...children,
      //         index > 0 && <br key={index} />,
      //         textSegment,
      //       ],
      //       []
      //     ),
    })
  )
}

function usePalette() {
  const data = useStaticQuery(graphql`
    {
      allContentfulPalette {
        nodes {
          primary
          light
          lightest
          dark
          darkest
        }
      }
    }
  `)
  return data.allContentfulPalette.nodes
}

const RichText = ({ className, field, content, color }) => {
  const palettes = usePalette()

  return (
    <>
      {content ? (
        <div className={className}>
          <RichTextContent
            content={content}
            color={color}
            palettes={palettes}
          />
        </div>
      ) : field ? (
        <div
          className={className}
          dangerouslySetInnerHTML={{ __html: field.childMarkdownRemark.html }}
        />
      ) : null}
    </>
  )
}

export const getPlainText = raw => {
  const json = JSON.parse(raw)
  return documentToPlainTextString(json)
}

export default RichText
